import {
  type Arrow,
  type ArrowLabel,
  type ArrowModel,
  type ArrowStyleType,
  type ID,
  ProjectActions,
} from '@octopus-do/octopus-project-core'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  artBoardActions,
  selectArrowSource,
  selectSelectedArrowLabel,
} from '@store/reducers/artboard'
import { getId } from '@store/utils'
import * as ActionTypes from '@storeOld/actions'
import { COMMIT_PROJECT_REQUEST } from '@storeOld/actions'
import {
  CanvasActionTypes,
  setSelectedArrowPointId,
} from '@storeOld/reducers/canvas/canvas'
import { notificationActionCreators } from '@storeOld/reducers/notification'

import { getBaseArrowPoints } from '@/utils/arrows'

export const thunkAddArrow =
  (targetId: string, model: ArrowModel): AppThunk =>
  (dispatch, getState) => {
    const state = getState()

    // Источник берём из режима стрелок, а не из выделения: от страницы клик по
    // цели переносит выделение на неё саму (OCT-5140).
    const source = selectArrowSource(state)

    if (!source || source.id === targetId) {
      return
    }

    const sourceExists =
      source.kind === 'node'
        ? Boolean(
            migrationProjectSelector.getProjectNodeByIdUnsafe(source.id)(state)
          )
        : Boolean(
            migrationProjectSelector.getProjectBlockById(source.id)(state)
          )

    if (!sourceExists) {
      return
    }

    // todo review to find better selector to get arrow by relation
    const activeTab = migrationProjectSelector.getActiveOrFirstTabId(state)
    const arrows = migrationProjectSelector.getTabArrows(activeTab)(state)

    // Дубль запрещён только в том же направлении: встречная связь B → A это
    // отдельная стрелка со своим маршрутом.
    const existingArrow = arrows.find(
      (a: Arrow) => a.relation[0] === source.id && a.relation[1] === targetId
    )

    if (existingArrow) {
      dispatch(artBoardActions.changeMode('edit'))

      return
    }

    const id = getId()
    const arrow: Arrow = {
      id,
      relation: [source.id, targetId],
      model,
      points: [],
      offset: {
        x: 0,
        y: 0,
      },
      // Своего цвета у новой стрелки нет — красится от источника (OCT-5222).
      colorId: '',
      label: null,
      type: 'smooth',
      visible: true,
    }

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.arrows.add({
          items: [arrow],
          index: 0,
          parents: [source.id, targetId],
        }),
      ],
    })

    dispatch(artBoardActions.changeMode('edit'))
  }

export const thunkDeleteSelectedArrows =
  (): AppThunk => (dispatch, getState) => {
    const { canvas } = getState()
    const { selectedArrowId } = canvas.arrow

    if (!selectedArrowId) {
      return
    }

    dispatch(
      notificationActionCreators.setClearNotification({
        toastContainerId: 'main',
      })
    )

    dispatch({
      type: COMMIT_PROJECT_REQUEST,
      payload: [ProjectActions.arrows.delete({ ids: [selectedArrowId] })],
    })

    dispatch({
      type: CanvasActionTypes.RESET_SELECTED_ARROW_ID,
    })
  }

export const addArrowGridPoint =
  (
    arrowId: string,
    pointId: string,
    elementPos: any,
    index: number
  ): AppThunk =>
  (dispatch) => {
    dispatch({
      type: COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.arrows.addGridPoint({
          index,
          id: arrowId,
          item: {
            id: pointId,
            offset: {
              x: elementPos.x,
              y: elementPos.y,
            },
          },
        }),
      ],
    })
  }

export const thunkDeleteSelectedArrowGridPoint =
  (): AppThunk => (dispatch, getState) => {
    const {
      canvas,
      manager: {
        project: { arrows },
      },
    } = getState()

    const arrowId = canvas.arrow.selectedArrowId
    const pointId = canvas.arrow.selectedArrowGridPointId

    if (!arrowId || !pointId) {
      return
    }

    const arrow = arrows.find((a: Arrow) => a.id === arrowId)
    const points = arrow?.points

    const index = (points || []).findIndex((p) => p.id === pointId)

    if (index === -1) {
      return
    }

    dispatch(
      notificationActionCreators.setClearNotification({
        toastContainerId: 'main',
      })
    )

    dispatch({
      type: COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.arrows.deleteGridPoint({
          id: arrowId,
          item: pointId,
        }),
      ],
    })

    if (points!.length === 1) {
      dispatch({ type: CanvasActionTypes.RESET_SELECTED_ARROW_POINT_ID })
    } else {
      const nextIndex = index ? index - 1 : index + 1

      dispatch(setSelectedArrowPointId(points![nextIndex].id))
    }
  }

/**
 * Пустой colorId возвращает стрелку к цвету источника.
 *
 * Выделение сбрасывается, как и при смене типа: выбрал цвет — тулбар закрылся.
 */
export const thunkChangeArrowColor =
  ({ colorId, id }: { colorId: string; id: string }): AppThunk =>
  (dispatch, getState) => {
    const arrow = getState().manager.project.arrows.find(
      (a: Arrow) => a.id === id
    )

    // У стрелки, сохранённой до OCT-5222, поля нет вовсе, и это то же самое,
    // что пустая строка, — иначе «как у источника» на такой стрелке писало бы
    // экшен, который ничего не меняет.
    if (!arrow || (arrow.colorId ?? '') === colorId) {
      return
    }

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [ProjectActions.arrows.updateColor({ colorId, id })],
    })

    dispatch({
      type: CanvasActionTypes.RESET_SELECTED_ARROW_ID,
    })
  }

interface ArrowLabelUpdate {
  id: ID
  label: ArrowLabel | null
}

/** Null takes the label off. */
export const thunkUpdateArrowLabel =
  ({ id, label }: ArrowLabelUpdate): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const arrow = state.manager.project.arrows.find((a: Arrow) => a.id === id)

    if (!arrow || state.artboard.isDisabled) {
      return
    }

    const prev = arrow.label ?? null
    const unchanged =
      prev?.text === label?.text && prev?.position === label?.position

    if (unchanged) {
      return
    }

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [ProjectActions.arrows.updateLabel({ id, label })],
    })
  }

/** Delete on a selected label takes off the label, and the arrow stays. */
export const thunkDeleteSelectedArrowLabel =
  (): AppThunk => (dispatch, getState) => {
    const id = selectSelectedArrowLabel(getState())

    if (!id) {
      return
    }

    dispatch(thunkUpdateArrowLabel({ id, label: null }))
    dispatch({ type: CanvasActionTypes.RESET_SELECTED_ARROW_ID })
  }

export const thunkChangeArrowType =
  ({ id, type }: { id: string; type: ArrowStyleType }): AppThunk =>
  (dispatch, getState) => {
    const state = getState()

    const arrow = state.manager.project.arrows.find((a: Arrow) => a.id === id)
    const { scale } = state.canvas

    if (!arrow) {
      return
    }

    const { relation } = arrow

    const points = getBaseArrowPoints(type, relation[0], relation[1], scale)

    const pointActions = points.map((point, index) =>
      ProjectActions.arrows.addGridPoint({
        id,
        index,
        item: {
          id: getId(),
          offset: {
            x: point.x,
            y: point.y,
          },
        },
      })
    )

    dispatch({
      type: ActionTypes.COMMIT_PROJECT_REQUEST,
      payload: [
        ProjectActions.arrows.changeType({
          id,
          type,
        }),
        ...pointActions,
      ],
    })

    dispatch({
      type: CanvasActionTypes.RESET_SELECTED_ARROW_ID,
    })
  }
